import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router';
import {
    ArrowLeft,
    Package,
    Calendar,
    Upload01,
    Eye,
    Download01,
    Trash01,
    Clock,
    User01,
    CheckCircle,
    XCircle,
    AlertCircle,
    ClockRewind,
    SearchRefraction
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import RouteMap from '@/components/application/map/route-map';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Table, TableCard } from '@/components/application/table/table';
import { Tabs } from '@/components/application/tabs/tabs';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { containerService } from '@/data/mock-containers';
import { documentService } from '@/data/mock-documents';
import { accountService } from '@/data/mock-accounts';
import type { Container, Document } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';

interface TimelineEvent {
    id: string;
    timestamp: Date;
    event: string;
    user?: string;
    details?: string;
}

export const ReceiverContainerDetailPage = () => {
    const { t } = useTranslation(['pages:containers', 'common', 'types']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [container, setContainer] = useState<Container | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [shipper, setShipper] = useState<any>(null);
    const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            if (!id) return;

            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Load container
            const containerData = containerService.getById(id);
            if (!containerData) {
                setIsLoading(false);
                return;
            }
            setContainer(containerData);

            // Load documents
            const documentsData = documentService.getByContainerId(id);
            setDocuments(documentsData);

            // Load shipper info
            const shipperData = accountService.getById(containerData.shipperId);
            setShipper(shipperData);

            // Generate timeline events
            const timelineEvents: TimelineEvent[] = [
                {
                    id: `${containerData.id}-1`,
                    timestamp: containerData.createdAt,
                    event: t('detail.timeline.events.containerCreated'),
                    user: t('detail.info.shipper'),
                    details: `${t('detail.timeline.events.containerCreated')} ${containerData.containerNumber}`
                },
                {
                    id: `${containerData.id}-2`,
                    timestamp: new Date(containerData.createdAt.getTime() + 24 * 60 * 60 * 1000),
                    event: t('detail.timeline.events.statusChanged'),
                    user: 'System',
                    details: `${t('detail.timeline.statusChangedFrom')} "Created" ${t('detail.timeline.to')} "${translateContainerStatus(containerData.status)}"`
                },
                {
                    id: `${containerData.id}-3`,
                    timestamp: new Date(containerData.createdAt.getTime() + 2 * 24 * 60 * 60 * 1000),
                    event: t('detail.timeline.events.documentsUploaded'),
                    user: t('detail.info.shipper'),
                    details: `${documentsData.length} ${t('detail.timeline.documentsUploadedCount')}`
                },
                {
                    id: `${containerData.id}-4`,
                    timestamp: new Date(containerData.createdAt.getTime() + 3 * 24 * 60 * 60 * 1000),
                    event: t('detail.timeline.events.progressUpdated'),
                    user: 'System',
                    details: `${t('detail.timeline.progressUpdatedTo')} ${containerData.progress}%`
                }
            ];
            setTimeline(timelineEvents);

            setIsLoading(false);
        };

        loadData();
    }, [id]);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Completo': return CheckCircle;
            case 'Pending': return Clock;
            case 'Error': return XCircle;
            default: return AlertCircle;
        }
    };


    const handleViewDocument = (document: Document) => {
        setSelectedDocument(document);
        setShowDocumentModal(true);
    };

    const handleDeleteDocument = (documentId: string) => {
        setDocumentToDelete(documentId);
    };

    const handleConfirmDelete = () => {
        if (documentToDelete) {
            const success = documentService.delete(documentToDelete);
            if (success) {
                setDocuments(documents.filter(doc => doc.id !== documentToDelete));
            }
            setDocumentToDelete(null);
        }
    };

    const handleUploadDocument = () => {
        // TODO: Implement document upload functionality
        alert(t('detail.documents.uploadInDevelopment'));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading')}</p>
            </div>
        );
    }

    if (!container) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('detail.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('detail.notFound.message')}</p>
                <Link
                    to={(() => {
                        const viewParam = searchParams.get('view');
                        return viewParam ? `/receiver/containers?view=${viewParam}` : '/receiver/containers';
                    })()}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('detail.backToList')}
                </Link>
            </div>
        );
    }

    const tabItems = [
        { id: 'info', label: t('detail.tabs.info') },
        { id: 'documents', label: t('detail.tabs.documents') },
        { id: 'timeline', label: t('detail.tabs.timeline') }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to={(() => {
                            const viewParam = searchParams.get('view');
                            return viewParam ? `/receiver/containers?view=${viewParam}` : '/receiver/containers';
                        })()}
                        className="text-tertiary hover:text-primary"
                    >
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{container.containerNumber}</h1>
                        <p className="text-tertiary mt-1">
                            {container.route.from} → {container.route.to}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab 1: Info */}
                <Tabs.Panel id="info" className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Container Info */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.containerInfo')}</h2>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.status')}</span>
                                    <Badge color={getStatusColor(container.status)} size="sm">
                                        {translateContainerStatus(container.status)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.progress')}</span>
                                    <div className="flex items-center gap-2">
                                        <ProgressBar
                                            value={container.progress}
                                            min={0}
                                            max={100}
                                        />
                                        <span className="text-sm text-primary">{container.progress}%</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.riskLevel')}</span>
                                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                        {translateRiskLevel(container.riskLevel)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.documentStatus')}</span>
                                    <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                        {translateDocumentStatus(container.documentStatus)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.eta')}</span>
                                    <span className="text-sm text-primary">
                                        {container.eta.toLocaleDateString('it-IT')}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.value')}</span>
                                    <span className="text-sm text-primary">
                                        {container.value.currency} {container.value.amount.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.shippingCompany')}</span>
                                    <span className="text-sm text-primary">
                                        {container.shippingCompany ?? '—'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Route Info */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.route')}</h2>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Package className="size-5 text-brand-600" />
                                    <div>
                                        <div className="text-sm font-medium text-primary">{t('detail.info.from')}</div>
                                        <div className="text-sm text-tertiary">{container.route.from}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Package className="size-5 text-success-600" />
                                    <div>
                                        <div className="text-sm font-medium text-primary">{t('detail.info.to')}</div>
                                        <div className="text-sm text-tertiary">{container.route.to}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar className="size-5 text-tertiary" />
                                    <div>
                                        <div className="text-sm font-medium text-primary">{t('detail.info.created')}</div>
                                        <div className="text-sm text-tertiary">
                                            {container.createdAt.toLocaleDateString('it-IT')}
                                        </div>
                                    </div>
                                </div>
                                {/* Route Map */}
                                <div className="mt-4 h-72 rounded-lg overflow-hidden">
                                    <RouteMap
                                        className="h-full"
                                        container={container}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipper Info */}
                    {shipper && (
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.shipper')}</h2>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <div className="text-sm font-medium text-primary">{shipper.legalName}</div>
                                    <div className="text-sm text-tertiary">{shipper.email}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-tertiary">{t('common.country')}</div>
                                    <div className="text-sm text-primary">{shipper.country}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </Tabs.Panel>

                {/* Tab 2: Documents */}
                <Tabs.Panel id="documents" className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-primary">{t('detail.documents.title')}</h2>
                        <Button
                            size="md"
                            color="primary"
                            className="inline-flex items-center gap-2"
                            onClick={handleUploadDocument}
                            iconLeading={Upload01}
                        >
                            {t('detail.actions.uploadDocuments')}
                        </Button>
                    </div>

                    <TableCard.Root>
                        <Table>
                            <Table.Header>
                                <Table.Head label={t('detail.documents.headers.name')} isRowHeader />
                                <Table.Head label={t('detail.documents.headers.type')} />
                                <Table.Head label={t('detail.documents.headers.status')} />
                                <Table.Head label={t('detail.documents.headers.uploaded')} />
                                <Table.Head label={t('detail.documents.headers.actions')} />
                            </Table.Header>
                            <Table.Body>
                                {documents.map((document) => {
                                    const StatusIcon = getStatusIcon(document.status);
                                    return (
                                        <Table.Row key={document.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{document.name}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color="brand" size="sm">
                                                    {documentService.getDocumentTypeDisplayName(document.type)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <StatusIcon className="size-4" />
                                                    <Badge color={getDocumentStatusColor(document.status)} size="sm">
                                                        {translateDocumentStatus(document.status)}
                                                    </Badge>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {document.uploadedAt.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Dropdown.Root>
                                                    <Dropdown.DotsHorizontalButton />
                                                    <Dropdown.Popover className="w-min">
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item
                                                                id="view"
                                                                icon={Eye}
                                                                label={t('detail.documents.actions.view')}
                                                                onAction={() => handleViewDocument(document)}
                                                            />
                                                            <Dropdown.Item
                                                                id="review"
                                                                icon={SearchRefraction}
                                                                label={t('detail.documents.actions.review')}
                                                                onAction={() => navigate(`/receiver/containers/${id}/documents/${document.id}/review`)}
                                                            />
                                                            <Dropdown.Item
                                                                id="history"
                                                                icon={ClockRewind}
                                                                label={t('detail.documents.actions.history')}
                                                                onAction={() => navigate(`/receiver/containers/${id}/documents/${document.id}/history`)}
                                                            />
                                                            <Dropdown.Item
                                                                id="download"
                                                                icon={Download01}
                                                                label={t('detail.documents.actions.download')}
                                                                onAction={() => {
                                                                    // TODO: Implement download functionality
                                                                }}
                                                            />
                                                            <Dropdown.Item
                                                                id="delete"
                                                                icon={Trash01}
                                                                label={t('detail.documents.actions.delete')}
                                                                onAction={() => handleDeleteDocument(document.id)}
                                                            />
                                                        </Dropdown.Menu>
                                                    </Dropdown.Popover>
                                                </Dropdown.Root>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                </Tabs.Panel>

                {/* Tab 3: Timeline */}
                <Tabs.Panel id="timeline" className="space-y-6">
                    <h2 className="text-lg font-semibold text-primary">{t('detail.timeline.title')}</h2>
                    <div className="space-y-4">
                        {timeline.map((event) => (
                            <div key={event.id} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                    <div className="rounded-full bg-brand-100 p-2">
                                        <Clock className="size-4 text-brand-600" />
                                    </div>
                                    <div className="w-px h-8 bg-secondary mt-2" />
                                </div>
                                <div className="flex-1 pb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium text-primary">{event.event}</span>
                                        <span className="text-xs text-tertiary">
                                            {event.timestamp.toLocaleString('it-IT')}
                                        </span>
                                    </div>
                                    {event.user && (
                                        <div className="text-xs text-tertiary mb-1">
                                            <User01 className="size-3 inline mr-1" />
                                            {event.user}
                                        </div>
                                    )}
                                    {event.details && (
                                        <div className="text-sm text-tertiary">{event.details}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Confirm Delete Modal */}
            {documentToDelete && (
                <ModalOverlay isOpen={!!documentToDelete} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('detail.documents.confirmDelete.title')}</h2>
                                    <button
                                        onClick={() => setDocumentToDelete(null)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary mb-6">{t('detail.documents.confirmDelete.message')}</p>
                                <div className="flex justify-end gap-3">
                                    <Button onClick={() => setDocumentToDelete(null)} color="secondary" size="md">
                                        {t('detail.documents.confirmDelete.cancel')}
                                    </Button>
                                    <Button onClick={handleConfirmDelete} color="primary-destructive" size="md">
                                        {t('detail.documents.confirmDelete.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Document Modal */}
            {showDocumentModal && selectedDocument && (
                <ModalOverlay isOpen={showDocumentModal} onOpenChange={setShowDocumentModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary mr-8 break-words">
                                        Dettagli Documento - {selectedDocument.name}
                                    </h2>
                                    <button
                                        onClick={() => setShowDocumentModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    {/* Document Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-sm font-medium text-primary">Tipo</div>
                                            <div className="text-sm text-tertiary">
                                                {documentService.getDocumentTypeDisplayName(selectedDocument.type)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-primary">Status</div>
                                            <Badge color={getDocumentStatusColor(selectedDocument.status)} size="sm">
                                                {selectedDocument.status}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Extracted Data */}
                                    <div>
                                        <h3 className="text-md font-semibold text-primary mb-3">Dati Estratti</h3>
                                        <div className="space-y-2">
                                            {Object.entries(selectedDocument.extractedData).map(([key, value]) => (
                                                <div key={key} className="flex justify-between py-2 border-b border-secondary">
                                                    <span className="text-sm font-medium text-primary">{key}</span>
                                                    <span className="text-sm text-tertiary">{String(value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Missing Fields */}
                                    {selectedDocument.missingFields.length > 0 && (
                                        <div>
                                            <h3 className="text-md font-semibold text-primary mb-3 text-error-600">
                                                Campi Mancanti
                                            </h3>
                                            <div className="space-y-1">
                                                {selectedDocument.missingFields.map((field) => (
                                                    <div key={field} className="flex items-center gap-2 text-sm text-error-600">
                                                        <XCircle className="size-4" />
                                                        {field}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Document Type Indicators */}
                                    <div>
                                        <h3 className="text-md font-semibold text-primary mb-3">Indicatori per Tipo</h3>
                                        <div className="space-y-1">
                                            {documentService.getDocumentTypeIndicators(selectedDocument.type).map((indicator) => (
                                                <div key={indicator} className="text-sm text-tertiary">
                                                    • {indicator}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
